import * as React from 'react';
import { Messages } from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { cssClass } from '../../../../style';
import { ConceptName } from '../../../types/ConceptName';
import { ValueConcept } from '../../../types/ValueConcept';
import { SynonymsChipPicker } from '../../../utils/chip-pickers/SynonymChipPicker';
import { LazyListWidget } from '../../../utils/lazy-list/LazyList';
import { compareValueConcepts, formatValue, highlightSearch, valueConceptMatchesSearch } from '../../../utils/searchUtils';
import { useIndexedState } from '../../../utils/useIndexedState';
import { ValuesAndSynonymsHeader } from './ValuesAndSynonymsHeader';

const rowHeight = '39px';
const cssPrefix = 'DataroleValuesAndSynonymsList';
const rowStyle =  cssClass(cssPrefix, 'singleRow', {
  borderBottom: '1px solid rgb(235, 235, 235)',
  minHeight: rowHeight,
});

const valueStyle =  cssClass(cssPrefix, 'rowValue', {
  paddingLeft: CommonStyles.Sizes.DefaultSpace,
  display: 'inline-block',
  width: '33%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
});

const synonymsStyle =  cssClass(cssPrefix, 'rowSynonyms', {
  paddingLeft: CommonStyles.Sizes.DefaultSpace,
  display: 'inline-block',
  width: '67%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
  verticalAlign: 'bottom',
});

export interface DataRoleValuesAndSynonymsListProps {
  readonly valueConcepts: ReadonlyArray<ValueConcept>;
  readonly isEditing: boolean;
  readonly filterText: string;
}

export const DataRoleValuesAndSynonymsList: React.FC<DataRoleValuesAndSynonymsListProps> = (props: DataRoleValuesAndSynonymsListProps) => {
  const orderedValueConcepts: ReadonlyArray<ValueConcept> = [...props.valueConcepts].sort(compareValueConcepts);
  const [synonyms, setSynonym] = useIndexedState(orderedValueConcepts.map(v => v.names));
  const displayedValuesAndSynonyms: ValueConcept[] = orderedValueConcepts
    .filter(v => valueConceptMatchesSearch(v, props.filterText));
  return <>
    <ValuesAndSynonymsHeader/>
    <LazyListWidget
      rowCount={props.valueConcepts.length}
      rowHeight={39}
      rowToComponent={props.isEditing?
        renderRoleValueWithEditableSynonyms(setSynonym, props.filterText, props.valueConcepts) :
        renderRoleValueWithConstSynonyms(props.filterText, props.valueConcepts)}/>
    </>;
};


const renderRoleValueWithEditableSynonyms = (setSynonym: (row: number, values: ConceptName[]) => void, searchText: string, valueConcepts: ReadonlyArray<ValueConcept>) => (row: number): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueConcepts[row].value), searchText)}</div>
    <div className={synonymsStyle} style={{padding: 0}}>
      <SynonymsChipPicker
        placeholder={''}
        label={Messages.dataRoleDefinitionSubspace_Values_SynonymEdit_Label({VALUE: formatValue(valueConcepts[row].value)})}
        testId={`synonyms-for-${JSON.stringify(valueConcepts[row])}`}
        values={valueConcepts[row].names}
        onChange={(values: ConceptName[]) => setSynonym(row, values)}
        style={{border: 'none'}}
        chipContentFormatter={(value: string) => highlightSearch(value, searchText)}
        getErrorMessage={(e: string) => ''}/>
    </div>
  </div>
);

const renderRoleValueWithConstSynonyms = (searchText: string, valueConcepts: ReadonlyArray<ValueConcept>) => (row: number): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueConcepts[row].value), searchText)}</div>
    <div className={synonymsStyle}>
      {highlightSearch(valueConcepts[row].names.map(n => n.name).join(', '), searchText)}
      </div>
  </div>
);

